import { addCalendarDays, isValidCalendarDate } from './dates';
import type { InvoiceStatus } from './types';

export interface ReceivableInvoice {
  status: InvoiceStatus;
  project_voided_at: number | null;
  deleted_at?: number | null;
  issue_date: string | null;
  due_date: string | null;
}

/** Shared with BAS/outstanding: paid revenue survives a voided project. */
export function includedInFinance(invoice: Pick<ReceivableInvoice, 'status' | 'project_voided_at' | 'deleted_at'>): boolean {
  return invoice.deleted_at == null && invoice.status !== 'void'
    && (invoice.project_voided_at == null || invoice.status === 'paid');
}

export function isOutstanding(invoice: Pick<ReceivableInvoice, 'status'>): boolean {
  return invoice.status === 'sent' || invoice.status === 'overdue';
}

export function isUnpaidReceivable(invoice: ReceivableInvoice): boolean {
  return includedInFinance(invoice) && isOutstanding(invoice);
}

/** No client terms field exists. Derive for reporting only; never persist dates. */
export function effectiveDueDate(invoice: Pick<ReceivableInvoice, 'issue_date' | 'due_date'>): string | null {
  if (invoice.due_date && isValidCalendarDate(invoice.due_date)) return invoice.due_date;
  if (invoice.issue_date && isValidCalendarDate(invoice.issue_date)) return addCalendarDays(invoice.issue_date, 14);
  return null;
}

export type MissingInvoiceDate = 'issue' | 'due' | 'both';
export function missingAgingDate(invoice: Pick<ReceivableInvoice, 'issue_date' | 'due_date'>): MissingInvoiceDate | null {
  const issueMissing = !invoice.issue_date || !isValidCalendarDate(invoice.issue_date);
  const dueMissing = !effectiveDueDate(invoice);
  return issueMissing ? (dueMissing ? 'both' : 'issue') : dueMissing ? 'due' : null;
}

export const AGING_BUCKETS = ['d0_30', 'd31_60', 'd61_90', 'd90_plus'] as const;
export type AgingBucket = typeof AGING_BUCKETS[number];
export const AGING_LABELS = ['0–30', '31–60', '61–90', '90+'] as const;
export interface AgingAmounts {
  invoice_count: number;
  d0_30_cents: number;
  d31_60_cents: number;
  d61_90_cents: number;
  d90_plus_cents: number;
  total_cents: number;
}
export interface AgingClient extends AgingAmounts {
  client_id: string;
  client_name: string;
}
export interface AgingUndated {
  invoice_id: string;
  number: string;
  client_name: string;
  amount_cents: number;
  missing: MissingInvoiceDate;
}
export interface AgingReport {
  as_of: string;
  buckets: Record<AgingBucket, { inc_cents: number; ex_cents: number; invoice_count: number }>;
  clients: AgingClient[];
  totals: AgingAmounts & { total_ex_cents: number; client_count: number };
  undated: AgingUndated[];
  undated_total_cents: number;
}
